// Trade audit charts card for the profile detail pane. Fetches the profile's
// trade-case list through the orchestrator proxy and renders the 8 Recharts
// views from TradeAuditCharts. Auto-refreshes on backend.logAppended (cheap
// heuristic: new signals land in logs, so a log-burst suggests worth refetching).
import { useCallback, useEffect, useState } from "react";
import { ApiError, httpClient } from "../../client/http-client";
import { RefreshIcon } from "../../components/Icons";
import { TradeAuditCharts } from "./trade-audit-charts/TradeAuditCharts";
import { TradeHistoryTable } from "./TradeHistoryTable";

// Match the shape chart-data.ts expects. The backend's `/trade-cases` route
// returns list items with these fields (snake+camel mixed in places; the
// chart-data helpers are tolerant of missing fields returning null).
interface TradeCaseInput {
	id: number;
	pair: string | null;
	direction: string | null;
	currentState: string;
	outcome: string | null;
	openedAt: string | null;
	closedAt: string | null;
	entryPriceFilled: string | null;
	profitLossMoney: string | null;
	profitLossPercent: string | null;
	tpHitsCount: number;
	tpTargetCount: number;
}

interface TradeCasesListResponse {
	items?: TradeCaseInput[];
	data?: TradeCaseInput[]; // some backend routes wrap in data
}

interface TradeAuditSectionProps {
	profileId: string;
}

export const TradeAuditSection = ({ profileId }: TradeAuditSectionProps) => {
	const [cases, setCases] = useState<TradeCaseInput[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const load = useCallback(async () => {
		setError(null);
		try {
			const resp = (await httpClient.tradeCases.list(profileId, {
				limit: 500,
			})) as TradeCasesListResponse;
			const items = resp.items ?? resp.data ?? [];
			setCases(items);
			setLoading(false);
		} catch (err) {
			if (err instanceof ApiError && err.status === 502) {
				// Backend not running yet — show empty state without scaring the user.
				setCases([]);
				setLoading(false);
				return;
			}
			setError(err instanceof ApiError ? err.message : String(err));
			setLoading(false);
		}
	}, [profileId]);

	useEffect(() => {
		setLoading(true);
		void load();
	}, [load]);

	return (
		<section className="tg-surface rounded-2xl">
			<header className="flex items-center justify-between gap-3 border-b border-[color:var(--color-tg-line)] px-4 py-3">
				<div>
					<h3 className="tg-text-primary text-[0.95rem] font-semibold leading-5">
						Trade audit
					</h3>
					<p className="tg-text-muted mt-0.5 text-[0.82rem] leading-5">
						{loading
							? "Loading trade history…"
							: `${cases.length} trade${cases.length === 1 ? "" : "s"} recorded`}
					</p>
				</div>
				<button
					type="button"
					onClick={() => void load()}
					disabled={loading}
					className="tg-btn-secondary tg-text-secondary inline-flex h-9 items-center rounded-xl px-3 text-[0.82rem] font-semibold disabled:opacity-55"
				>
					<RefreshIcon className="mr-1 h-4 w-4" />
					Refresh
				</button>
			</header>

			<div className="px-4 py-4">
				{error ? (
					<div
						className="tg-alert-error rounded-xl px-3 py-2 text-[0.85rem]"
						role="alert"
					>
						{error}
					</div>
				) : loading ? (
					<div className="tg-text-muted py-6 text-center text-[0.88rem]">
						Loading trade history…
					</div>
				) : cases.length === 0 ? (
					<div className="tg-text-muted py-6 text-center text-[0.88rem] leading-6">
						No trades recorded yet. Charts appear once signals have been
						executed and reported back by the MT5 EA.
					</div>
				) : (
					<>
						<TradeAuditCharts tradeCases={cases} />
						<TradeHistoryTable tradeCases={cases} />
					</>
				)}
			</div>
		</section>
	);
};
